// Script lexer (SCR-1). PURE (ENG-1) — no imports beyond the error type it owns.
//
// Turns source text into a flat token stream with 1-based line/column and 0-based
// index positions (so parse errors can point at the offending character, cf. the
// command parser's "at position N"). The grammar is deliberately tiny: numbers,
// double/single-quoted strings, identifiers/keywords, and a fixed operator set.
// `//` line comments are skipped. Any stray character is a positioned ScriptError.

export interface Pos {
  /** 1-based line. */ line: number;
  /** 1-based column. */ col: number;
  /** 0-based character offset. */ index: number;
}

/** The one error type the whole script pipeline raises. `pos` is set for lex/parse
 *  errors (a source location) and absent for host/runtime aborts. */
export class ScriptError extends Error {
  readonly pos?: Pos;
  constructor(message: string, pos?: Pos) {
    super(pos ? `${message} (line ${pos.line}, col ${pos.col})` : message);
    this.name = 'ScriptError';
    this.pos = pos;
  }
}

export type TokenKind = 'num' | 'str' | 'ident' | 'keyword' | 'op' | 'eof';

export interface Token {
  kind: TokenKind;
  /** Raw text (operator/keyword/identifier) — for `num`/`str` see `num`/`str`. */
  text: string;
  num?: number;
  str?: string;
  pos: Pos;
}

// Reserved words. `fn`/`return` joined the set with user-defined functions (SCR-5,
// ARCH §27.2); no v1 source used either as an identifier, so the language stays a
// strict superset of v1.
export const KEYWORDS = new Set(['let', 'if', 'else', 'repeat', 'on', 'true', 'false', 'fn', 'return', 'capabilities']);

// Multi-character operators, matched before their single-character prefixes.
const OPS2 = ['==', '!=', '<=', '>=', '&&', '||'];
const OPS1 = new Set(['+', '-', '*', '/', '%', '<', '>', '!', '=', '(', ')', '{', '}', ',', '.']);

export function tokenize(src: string): Token[] {
  const toks: Token[] = [];
  let i = 0;
  let line = 1;
  let col = 1;
  const at = (): Pos => ({ line, col, index: i });
  const adv = (n = 1): void => {
    for (let k = 0; k < n; k++) {
      if (src[i] === '\n') { line++; col = 1; } else { col++; }
      i++;
    }
  };

  while (i < src.length) {
    const c = src[i]!;
    // Whitespace.
    if (c === ' ' || c === '\t' || c === '\r' || c === '\n') { adv(); continue; }
    // Line comment.
    if (c === '/' && src[i + 1] === '/') {
      while (i < src.length && src[i] !== '\n') adv();
      continue;
    }
    // Number: digits with an optional single decimal point.
    if (c >= '0' && c <= '9') {
      const start = at();
      let text = '';
      while (i < src.length && src[i]! >= '0' && src[i]! <= '9') { text += src[i]; adv(); }
      if (src[i] === '.' && src[i + 1]! >= '0' && src[i + 1]! <= '9') {
        text += '.'; adv();
        while (i < src.length && src[i]! >= '0' && src[i]! <= '9') { text += src[i]; adv(); }
      }
      toks.push({ kind: 'num', text, num: Number(text), pos: start });
      continue;
    }
    // String: "…" or '…' with \" \\ \n \t escapes.
    if (c === '"' || c === "'") {
      const start = at();
      const quote = c;
      adv();
      let str = '';
      while (i < src.length && src[i] !== quote) {
        if (src[i] === '\n') throw new ScriptError('unterminated string', start);
        if (src[i] === '\\') {
          adv();
          const e = src[i];
          str += e === 'n' ? '\n' : e === 't' ? '\t' : e === '\\' ? '\\' : e === quote ? quote : (e ?? '');
          adv();
          continue;
        }
        str += src[i];
        adv();
      }
      if (i >= src.length) throw new ScriptError('unterminated string', start);
      adv(); // closing quote
      toks.push({ kind: 'str', text: str, str, pos: start });
      continue;
    }
    // Identifier / keyword.
    if (isIdentStart(c)) {
      const start = at();
      let text = '';
      while (i < src.length && isIdentPart(src[i]!)) { text += src[i]; adv(); }
      toks.push({ kind: KEYWORDS.has(text) ? 'keyword' : 'ident', text, pos: start });
      continue;
    }
    // Operators / punctuation.
    const two = src.slice(i, i + 2);
    if (OPS2.includes(two)) {
      const start = at();
      adv(2);
      toks.push({ kind: 'op', text: two, pos: start });
      continue;
    }
    if (OPS1.has(c)) {
      const start = at();
      adv();
      toks.push({ kind: 'op', text: c, pos: start });
      continue;
    }
    throw new ScriptError(`unexpected character '${c}'`, at());
  }
  toks.push({ kind: 'eof', text: '', pos: at() });
  return toks;
}

function isIdentStart(c: string): boolean {
  return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
}
function isIdentPart(c: string): boolean {
  return isIdentStart(c) || (c >= '0' && c <= '9');
}
